// Local Archive Service - Manages browser-based order archive
// Uses localStorage for persistence with automatic cleanup of old orders

class LocalArchiveService {
  constructor() {
    this.STORAGE_KEY = 'table_tech_archive';
    this.MAX_ORDERS = 500; // Maximum orders to keep in local storage
    this.RETENTION_HOURS = 8; // Keep orders for 8 hours only
  }

  // Initialize and clean up old data
  init() {
    this.cleanupOldOrders();
    return this.getAllOrders();
  }

  // Save a completed order to local archive
  saveOrder(order) {
    try {
      const orders = this.getAllOrders();
      
      // Add metadata
      const archivedOrder = {
        ...order,
        archivedAt: new Date().toISOString(),
        archivedLocally: true
      };
      
      // Add to beginning of array (newest first)
      orders.unshift(archivedOrder);
      
      // Limit the number of stored orders
      if (orders.length > this.MAX_ORDERS) {
        orders.splice(this.MAX_ORDERS);
      }
      
      // Save to localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(orders));
      
      return archivedOrder;
    } catch (error) {
      console.error('Error saving order to local archive:', error);
      // If localStorage is full, try to clean up and retry
      if (error.name === 'QuotaExceededError') {
        this.cleanupOldOrders();
        try {
          const orders = this.getAllOrders();
          orders.unshift(order);
          orders.splice(Math.floor(this.MAX_ORDERS / 2)); // Keep only half
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(orders));
          return order;
        } catch (retryError) {
          console.error('Failed to save after cleanup:', retryError);
          return null;
        }
      }
      return null;
    }
  }

  // Get all orders from local archive
  getAllOrders() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error reading local archive:', error);
      return [];
    }
  }

  // Get orders for a specific restaurant and station
  getOrdersByStation(restaurantId, stationId) {
    const orders = this.getAllOrders();
    return orders.filter(order => 
      order.restaurantId === restaurantId && 
      order.stationId === stationId
    );
  }

  // Get a single order by ID
  getOrderById(orderId) {
    const orders = this.getAllOrders();
    return orders.find(order => order.id === orderId);
  }

  // Search orders
  searchOrders(query) {
    const orders = this.getAllOrders();
    const searchTerm = query.toLowerCase();
    
    return orders.filter(order => {
      // Search in order number
      if (order.orderNumber && order.orderNumber.toString().includes(searchTerm)) {
        return true;
      }
      // Search in table number
      if (order.tableNumber && order.tableNumber.toString().toLowerCase().includes(searchTerm)) {
        return true;
      }
      // Search in items
      if (order.items && Array.isArray(order.items)) {
        return order.items.some(item => 
          item.name && item.name.toLowerCase().includes(searchTerm)
        );
      }
      return false;
    });
  }

  // Get orders from today
  getTodaysOrders() {
    const orders = this.getAllOrders();
    const today = new Date().toDateString();
    
    return orders.filter(order => {
      const orderDate = new Date(order.archivedAt || order.timestamp);
      return orderDate.toDateString() === today;
    });
  }

  // Get orders by date range
  getOrdersByDateRange(startDate, endDate) {
    const orders = this.getAllOrders();
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    
    return orders.filter(order => {
      const orderTime = new Date(order.archivedAt || order.timestamp).getTime();
      return orderTime >= start && orderTime <= end;
    });
  }

  // Delete a specific order
  deleteOrder(orderId) {
    try {
      const orders = this.getAllOrders();
      const filteredOrders = orders.filter(order => order.id !== orderId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredOrders));
      return true;
    } catch (error) {
      console.error('Error deleting order:', error);
      return false;
    }
  }

  // Clean up orders older than retention period (8 hours)
  cleanupOldOrders() {
    try {
      const orders = this.getAllOrders();
      const cutoffDate = new Date();
      cutoffDate.setHours(cutoffDate.getHours() - this.RETENTION_HOURS);
      
      const recentOrders = orders.filter(order => {
        const orderDate = new Date(order.archivedAt || order.timestamp);
        return orderDate > cutoffDate;
      });
      
      if (recentOrders.length < orders.length) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(recentOrders));
        console.log(`Cleaned up ${orders.length - recentOrders.length} orders older than ${this.RETENTION_HOURS} hours`);
      }
      
      return recentOrders;
    } catch (error) {
      console.error('Error cleaning up orders:', error);
      return this.getAllOrders();
    }
  }

  // Clear all local archive data
  clearAll() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('Error clearing archive:', error);
      return false;
    }
  }

  // Get storage size info
  getStorageInfo() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY) || '[]';
      const sizeInBytes = new Blob([data]).size;
      const orders = JSON.parse(data);
      
      return {
        orderCount: orders.length,
        sizeInBytes,
        sizeInKB: (sizeInBytes / 1024).toFixed(2),
        sizeInMB: (sizeInBytes / 1024 / 1024).toFixed(2),
        percentUsed: ((sizeInBytes / (5 * 1024 * 1024)) * 100).toFixed(1) // Assume 5MB limit
      };
    } catch (error) {
      console.error('Error getting storage info:', error);
      return {
        orderCount: 0,
        sizeInBytes: 0,
        sizeInKB: '0',
        sizeInMB: '0',
        percentUsed: '0'
      };
    }
  }

  // Export archive data as JSON
  exportArchive() {
    const orders = this.getAllOrders();
    const dataStr = JSON.stringify(orders, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `table-tech-archive-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  // Import archive data from JSON
  async importArchive(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const importedOrders = JSON.parse(e.target.result);
          
          if (!Array.isArray(importedOrders)) {
            throw new Error('Invalid archive format');
          }
          
          const currentOrders = this.getAllOrders();
          const mergedOrders = [...importedOrders, ...currentOrders];
          
          // Remove duplicates based on order ID
          const uniqueOrders = mergedOrders.filter((order, index, self) =>
            index === self.findIndex(o => o.id === order.id)
          );
          
          // Sort by date and limit
          uniqueOrders.sort((a, b) => 
            new Date(b.archivedAt || b.timestamp) - new Date(a.archivedAt || a.timestamp)
          );
          uniqueOrders.splice(this.MAX_ORDERS);
          
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(uniqueOrders));
          resolve(uniqueOrders.length);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }
}

// Create singleton instance
const localArchiveService = new LocalArchiveService();

export default localArchiveService;